/**
 * Pure helpers to align free-text / AI labels with tenant-managed expense categories.
 */

export interface TenantExpenseCategoryRef {
  expenseCategoryId: string;
  name: string;
}

export function normalizeExpenseCategoryComparable(value: string): string {
  return value.trim().toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

function tokenizeExpenseCategoryLabel(value: string): string[] {
  return normalizeExpenseCategoryComparable(value)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 3);
}

function tokensOverlap(tokenA: string, tokenB: string): boolean {
  if (tokenA === tokenB) return true;
  if (tokenA.length >= 4 && tokenB.startsWith(tokenA)) return true;
  if (tokenB.length >= 4 && tokenA.startsWith(tokenB)) return true;
  return false;
}

function scoreExpenseCategoryTokenOverlap(label: string, categoryName: string): number {
  const labelTokens = tokenizeExpenseCategoryLabel(label);
  if (labelTokens.length === 0) return 0;

  const categoryTokens = tokenizeExpenseCategoryLabel(categoryName);
  if (categoryTokens.length === 0) return 0;

  let matched = 0;
  for (const labelToken of labelTokens) {
    if (categoryTokens.some((categoryToken) => tokensOverlap(labelToken, categoryToken))) {
      matched += 1;
    }
  }

  return matched / labelTokens.length;
}

/**
 * Exact case-insensitive name match (after normalization).
 */
export function matchExpenseCategoryByExactName(
  label: string,
  categories: TenantExpenseCategoryRef[],
): TenantExpenseCategoryRef | null {
  const comparable = normalizeExpenseCategoryComparable(label);
  if (!comparable) return null;
  return (
    categories.find(
      (category) => normalizeExpenseCategoryComparable(category.name) === comparable,
    ) ?? null
  );
}

/**
 * Fuzzy match: label equals category name, or one contains the other (min 3 chars).
 * Picks the longest category name when multiple match (more specific).
 */
export function matchExpenseCategoryByFuzzyLabel(
  label: string,
  categories: TenantExpenseCategoryRef[],
): TenantExpenseCategoryRef | null {
  const exact = matchExpenseCategoryByExactName(label, categories);
  if (exact) return exact;

  const comparable = normalizeExpenseCategoryComparable(label);
  if (comparable.length < 3) return null;

  const candidates = categories.filter((category) => {
    const nameComparable = normalizeExpenseCategoryComparable(category.name);
    if (nameComparable.length < 3) return false;
    return (
      nameComparable.includes(comparable) || comparable.includes(nameComparable)
    );
  });

  if (candidates.length === 0) return null;

  return [...candidates].sort((a, b) => b.name.length - a.name.length)[0] ?? null;
}

/**
 * Best-effort match for AI / free-text labels: exact, substring, then token overlap.
 * Returns the highest-scoring tenant category when overlap is strong enough.
 */
export function matchExpenseCategoryByBestLabel(
  label: string,
  categories: TenantExpenseCategoryRef[],
): TenantExpenseCategoryRef | null {
  if (categories.length === 0) return null;

  const exact = matchExpenseCategoryByExactName(label, categories);
  if (exact) return exact;

  const substring = matchExpenseCategoryByFuzzyLabel(label, categories);
  if (substring) return substring;

  const trimmed = label.trim();
  if (!trimmed) return null;

  const scored = categories
    .map((category) => ({
      category,
      score: scoreExpenseCategoryTokenOverlap(trimmed, category.name),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return b.category.name.length - a.category.name.length;
    });

  const best = scored[0];
  if (!best || best.score < 0.34) return null;

  return best.category;
}
